import { useTenantScope } from "../../contexts/TenantScopeContext";
/**
 * Compatibility wrapper for useStytchMemberSession that works in both:
 * - Cookie-based auth (normal app): Uses Stytch SDK directly
 * - Bearer token auth (iframe embed): Creates session from EmbedAuthContext
 *
 * This allows the same code to work in both authentication modes.
 */

import { useMemo } from "react";
import { useStytchMemberSession } from "@stytch/react/b2b";
import { useEmbedAuth } from "../../contexts/EmbedAuthContext";

interface MemberSession {
    expires_at: string;
}

interface MemberSessionHook {
    session: MemberSession | null;
    /**
     * False until the underlying check (Stytch's cookie-session validation)
     * has actually run. A fresh page load — a hard reload, or an admin link
     * opened via `window.open` into a brand-new tab — always starts with
     * `session: null` for the first render or two, before Stytch reports
     * back; a caller that redirects on `!session` without checking this
     * first bounces an authenticated user off the page (see PrivateWrapper).
     */
    isInitialized: boolean;
}

export function useCompatMemberSession(): MemberSessionHook {
    const tenantScope = useTenantScope();
    // Try Stytch SDK first (works in cookie-based auth)
    const stytchSession = useStytchMemberSession();

    // Check embed auth context (works in Bearer token auth)
    const embedAuth = useEmbedAuth();
    const exp = embedAuth.tokens?.exp;

    // Embed mode: create session object from Bearer tokens. Memoized on
    // `exp` alone so the returned reference is stable across renders that
    // don't actually change the token -- consumers (e.g. NativeNavBridge's
    // setNavVisible effect) depend on referential stability, not just value
    // equality.
    const embedSession = useMemo<MemberSession | null>(() => {
        if (!embedAuth.isAuthenticated || exp === undefined) {
            return null;
        }
        return { expires_at: new Date(exp * 1000).toISOString() };
    }, [embedAuth.isAuthenticated, exp]);

    if (tenantScope.mode === "canonical")
        return { session: tenantScope.session ?? null, isInitialized: !!tenantScope.session };

    // If Stytch SDK has a session, use it (normal cookie-based flow)
    if (stytchSession.session) {
        return { session: stytchSession.session, isInitialized: true };
    }

    // Embed mode falls through to `embedSession`; `null` when not
    // authenticated in either mode. Ready once Stytch's own cookie-session
    // check has run and, on a native host, once the page has read the
    // session it started with: until then a native page has no tokens even
    // when the member is signed in (NativeNavBridge would report signed out).
    return {
        session: embedSession,
        isInitialized: stytchSession.isInitialized && embedAuth.sessionRead,
    };
}
